import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';

import { palette } from '../../theme/palette';
import Iconify from '../../theme/iconify/iconify';

import Model from './Model';
import Prompt from './Prompt';
import Message from './Message';
import { mislecAPI } from '../../util/axios';

function Chat({ user, updateUser, fetchChats }) {
    const [chat, setChat] = React.useState(null);
    const [model, setModel] = React.useState('Qwen');
    const [corpus, setCorpus] = React.useState(null);
    const [fetched, setFetched] = React.useState(false);
    const [prompt, setPrompt] = React.useState('');
    const [typing, setTyping] = React.useState(false);

    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();

    React.useEffect(() => {
        window.scrollTo(0, document.body.scrollHeight);
    }, [chat]);

    React.useEffect(() => {
        fetchChat();
    }, [user, id]);

    const fetchChat = () => {
        if (!user) return;

        //  Fetch chat from the server
        mislecAPI.get(`/api/chat/${id}`)
            .then(res => {
                setChat(res.data);
                setModel(res.data.model || 'Qwen');
                setCorpus(res.data.corpus);
                setFetched(true);
            })
            .catch(error => {
                setTyping(false);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);

                //  Unauthorised access
                if (error.response)
                    if (error.response.data.detail === 'api.chat.get.chat.403') {
                        navigate('/chat');
                    }
            });
    }

    const sendMessage = () => {
        if (!user) return;
        if (prompt === '') return;

        //  Timestamp
        const timestamp = new Date().toISOString().replace(/T/, ' ').replace(/\..+/, '');

        //  Data preparation
        const data = {
            user: chat.user,
            title: chat.title,
            model: model,
            corpus: corpus,
            references: [],
            timestamp: chat.timestamp,
            last_modified: timestamp,
            messages: [...chat.messages, {
                role: 'USER',
                text: prompt,
                timestamp: new Date().toISOString().replace(/T/, ' ').replace(/\..+/, '')
            }]
        }
        setChat(data);
        setPrompt('');
        setTyping(true);

        //  Send data to the server
        mislecAPI.put(`/api/chat/${chat._id}/message`, data)
            .then(res => {
                setTyping(false);

                //  Chat information refresh
                fetchChat();

                //  Chat history refresh
                fetchChats();
            })
            .catch(error => {
                setTyping(false);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    if (!fetched) {
        if (!user) navigate('/login');
        fetchChat();
    }

    return (
        <>
            <Container
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    flex: 1,
                    mb: 0,
                    mt: 10
                }}
            >
                <Container sx={{ mb: { xs: 0, md: 2 }, px: 0 }}>
                    {chat &&
                        chat.messages.map((message, index) => (
                            <Message key={index} message={message} />
                        ))
                    }
                </Container>
                <Container sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'sticky',
                    bottom: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: '8px',
                    px: 0,
                    background: palette().background.default
                }}>
                    {typing && (
                        <>
                            <Grid
                                container
                                alignItems='center'
                                justifyContent='flex-start'
                                sx={{
                                    fontSize: '0.875rem',
                                    mb: 0.5,
                                    ml: 1
                                }}
                            >
                                {t('chat.message.typing')}
                                <Iconify
                                    icon='svg-spinners:3-dots-fade'
                                    style={{
                                        display: 'flex',
                                        alignSelf: 'flex-end',
                                        fontSize: '12px',
                                        height: '55%',
                                        width: 'auto',
                                        marginLeft: 1,
                                        marginBottom: 1,
                                    }}
                                />
                            </Grid>
                        </>
                    )}
                    <Prompt
                        user={user}
                        updateUser={updateUser}
                        prompt={prompt}
                        setPrompt={setPrompt}
                        sendMessage={sendMessage}
                        chatCorpus={corpus}
                        setChatCorpus={setCorpus}
                        model={<Model updateUser={updateUser} updateModel={(model) => setModel(model)} selectedModel={model} />}
                    />
                </Container>
            </Container>
        </>
    );
}

export default Chat;
